import { Alert } from '@ctw/shared/components/alert';
import { LegacyTable, type LegacyTableColumn } from '@ctw/shared/components/tables/legacy-table';
import { parseDelimitedText } from '@ctw/shared/content/document-viewer/_internal/renderers/text/delimited-text';
import { PlainTextRenderer } from '@ctw/shared/content/document-viewer/_internal/renderers/text/plain-text-renderer';
import {
  DOCUMENT_RENDERER_CONTENT_TEST_ID,
  DOCUMENT_VIEWER_TABLE_TEST_ID,
} from '@ctw/shared/test/ids';
import { tw } from '@ctw/shared/utils/tailwind';
import { memo, type RefObject, useMemo } from 'react';

export interface DelimitedTextRendererProps {
  text: string;
  title: string;
  contentRef: RefObject<HTMLDivElement | null>;
}

interface DelimitedTextRow {
  key: string;
  cells: Array<string>;
}

// Delimited files can run to thousands of rows; render a page at a time so the viewer stays
// responsive.
const PAGE_SIZE = 100;

export const DelimitedTextRenderer = memo(
  ({ text, title, contentRef }: DelimitedTextRendererProps) => {
    const table = useMemo(() => parseDelimitedText(text), [text]);

    const columns: Array<LegacyTableColumn<DelimitedTextRow>> = useMemo(
      () =>
        (table?.headers ?? []).map((header, index) => ({
          title: header.trim() === '' ? `Column ${index + 1}` : header,
          headerClassName: tw`whitespace-nowrap`,
          render: (row: DelimitedTextRow) => row.cells[index],
        })),
      [table],
    );

    const records: Array<DelimitedTextRow> = useMemo(
      () => (table?.rows ?? []).map((cells, index) => ({ key: String(index), cells })),
      [table],
    );

    if (!table) {
      return <PlainTextRenderer text={text} contentRef={contentRef} />;
    }

    return (
      <div
        ref={contentRef}
        data-testid={DOCUMENT_RENDERER_CONTENT_TEST_ID}
        className={tw`flex w-full flex-col gap-3 overflow-auto p-5`}
      >
        {table.hasRowsWiderThanHeaders && (
          <Alert type="caution">
            Some rows hold more values than the header row names. Those values appear in the unnamed
            columns on the right.
          </Alert>
        )}
        <LegacyTable
          id="delimited-text-table"
          testId={DOCUMENT_VIEWER_TABLE_TEST_ID}
          title={title}
          className={tw`!border w-full max-w-full rounded-md border-background-subtle`}
          columns={columns}
          records={records}
          spacing="tight"
          showTableHead={true}
          pagination={{ type: 'show-more', pageSize: PAGE_SIZE }}
          emptyMessage="This document has no rows."
          emptyStateShowHeaders={true}
        />
      </div>
    );
  },
);

DelimitedTextRenderer.displayName = 'DelimitedTextRenderer';
